// useNavigationPreloader.js - Hook to preload thumbnails before navigation
import { useState, useCallback, useRef } from 'react';
import { preloadThumbnails, isThumbnailPreloaded } from '../utils/thumbnailPreloader';

const useNavigationPreloader = (preloadedData, exploreDataReady, preloadExploreData) => {
  const [isPreloading, setIsPreloading] = useState(false);
  const [preloadProgress, setPreloadProgress] = useState(0);
  const [preloadMessage, setPreloadMessage] = useState('');
  const preloadTimeoutRef = useRef(null);
  const abortControllerRef = useRef(null);

  // Extract items that need thumbnails based on target page
  const getItemsForPage = useCallback((targetPath) => {
    if (!preloadedData) {
      console.log('âš ï¸ No preloaded data available for navigation preload');
      return [];
    }

    console.log('ðŸ” Getting items for path:', targetPath);
    
    // Parse the target path to determine which data to preload
    if (targetPath === '/courses') {
      const courses = preloadedData.courses || [];
      console.log(`ðŸ“š Found ${courses.length} courses to preload`);
      return courses;
    } else if (targetPath === '/lessons' || targetPath === '/problems') {
      const videos = preloadedData.recentVideos || [];
      console.log(`📹 Found ${videos.length} recent videos to preload`);
      return videos;
    } else if (targetPath === '/') {
      // Combine all home page data (was explore data)
      const exploreData = preloadedData.explore || {};
      const items = [
        ...(exploreData.courses || []),
        ...(exploreData.videos || []),
        ...(exploreData.solutions || [])
      ];
      console.log(`ðŸ  Found ${items.length} home page items to preload`);
      return items;
    }
    
    console.log('âš ï¸ No preload needed for path:', targetPath);
    return [];
  }, [preloadedData]);

  // Main preload function
  const preloadForNavigation = useCallback(async (targetPath) => {
    console.log('ðŸŽ¯ Starting preload for navigation to:', targetPath);
    
    // Clear any existing timeout
    if (preloadTimeoutRef.current) {
      clearTimeout(preloadTimeoutRef.current);
    }

    // Create abort controller for cancellation
    abortControllerRef.current = new AbortController();

    // Special handling for home page
    if (targetPath === '/' && !exploreDataReady) {
      console.log('â³ Explore data not ready, loading it first...');
      setIsPreloading(true);
      setPreloadMessage('Loading explore content...');
      
      // Trigger explore data loading if function is available
      if (preloadExploreData) {
        try {
          // Wait for explore data with timeout
          await Promise.race([
            preloadExploreData(),
            new Promise((resolve) => setTimeout(() => {
              console.warn('âš ï¸ Explore data load timeout');
              resolve(null);
            }, 3000))
          ]);
        } catch (error) {
          console.error('Error loading explore data:', error);
        }
      }
      
      // After loading (or timeout), check again for items
      const exploreItems = getItemsForPage(targetPath);
      console.log(`ðŸ“¦ After loading, found ${exploreItems.length} explore items to preload`);
      
      if (exploreItems.length === 0) {
        setIsPreloading(false);
        setPreloadMessage('');
        return { success: true, cached: 0, loaded: 0 };
      }
    }

    // Get items for the target page
    const items = getItemsForPage(targetPath);
    console.log(`ðŸ“¦ Found ${items.length} items to preload for ${targetPath}`);
    
    if (items.length === 0) {
      console.log('No items to preload for', targetPath);
      return { success: true, cached: 0, loaded: 0 };
    }

    setIsPreloading(true);
    setPreloadProgress(0);
    setPreloadMessage(`Loading content...`);

    try {
      // Start preloading with high priority and progress tracking
      const result = await Promise.race([
        // Main preload operation
        preloadThumbnails(items, {
          priority: 'high',
          concurrency: 15, // Increased concurrency for faster navigation
          onProgress: (stats) => {
            if (abortControllerRef.current?.signal.aborted) return;
            
            const progress = Math.round(stats.progress * 100);
            setPreloadProgress(progress);
            
            // Update message based on progress
            if (progress < 30) {
              setPreloadMessage('Preparing content...');
            } else if (progress < 70) {
              setPreloadMessage('Loading images...');
            } else {
              setPreloadMessage('Almost ready...');
            }
            
            console.log(`ðŸ“Š Preload progress: ${stats.loaded}/${stats.total} (${progress}%)`);
          }
        }),
        // Timeout promise (3 seconds max - increased for better loading)
        new Promise((resolve) => {
          preloadTimeoutRef.current = setTimeout(() => {
            console.warn('âš ï¸ Preload timeout - proceeding with partial load');
            resolve({ success: false, timeout: true });
          }, 3000);
        })
      ]);

      // Clear timeout if completed naturally
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current);
      }

      setIsPreloading(false);
      setPreloadProgress(100);
      setPreloadMessage('');

      if (result.timeout) {
        console.warn('Preload timed out, proceeding with navigation');
        return { success: false, timeout: true };
      }

      console.log(`âœ… Preload complete:`, result);
      return { success: true, ...result };
    } catch (error) {
      console.error('âŒ Preload error:', error);
      setIsPreloading(false);
      setPreloadProgress(0);
      setPreloadMessage('');
      return { success: false, error: error.message };
    }
  }, [getItemsForPage, exploreDataReady, preloadExploreData]);

  // Cancel ongoing preload
  const cancelPreload = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (preloadTimeoutRef.current) {
      clearTimeout(preloadTimeoutRef.current);
    }
    setIsPreloading(false);
    setPreloadProgress(0);
    setPreloadMessage('');
  }, []);

  // Quick check if items are already preloaded
  const checkIfPreloaded = useCallback((targetPath) => {
    const items = getItemsForPage(targetPath);
    if (items.length === 0) return true;

    // Check if all thumbnails are actually cached
    // We'll sample the first few for performance, but check actual cache status
    const sampleSize = Math.min(10, items.length);
    let cachedCount = 0;
    
    for (let i = 0; i < sampleSize; i++) {
      const item = items[i];
      // Check if thumbnail is actually in the cache
      if (isThumbnailPreloaded(item)) {
        cachedCount++;
      }
    }

    // Only consider preloaded if ALL sampled items are cached
    const isPreloaded = cachedCount === sampleSize;
    console.log(`ðŸ“Š Cache check for ${targetPath}: ${cachedCount}/${sampleSize} cached`);
    return isPreloaded;
  }, [getItemsForPage]);

  return {
    preloadForNavigation,
    cancelPreload,
    checkIfPreloaded,
    isPreloading,
    preloadProgress,
    preloadMessage,
    setPreloadProgress // Expose for manual progress control
  };
};

export default useNavigationPreloader;